import { getErrorCode } from "@onadssuper/api-client";
import { TASK_STATUS_LABELS } from "@onadssuper/validators";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, SearchX } from "lucide-react";
import type { ReactNode } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  PageHeader,
  PageSpinner,
} from "../../../../components/ui";
import { Conversation } from "../../../../features/comments/conversation";
import { useCurrentWorkspace } from "../../../../features/workspace/workspace-context";
import { useTRPC } from "../../../../lib/trpc";
import { formatCalendarDate, formatDateTime } from "../../../../lib/utils";

export const Route = createFileRoute("/w/$slug/campaigns/$campaignId_/tasks/$taskId")({
  component: TaskPage,
});

const Detail = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <dt className="text-xs font-medium tracking-wide text-subtle uppercase">{label}</dt>
    <dd className="mt-1">{children}</dd>
  </div>
);

/** One task: everything about it, what it waits on, and its comments. */
function TaskPage() {
  const { campaignId, taskId } = Route.useParams();
  const workspace = useCurrentWorkspace();
  const trpc = useTRPC();
  const task = useQuery(trpc.tasks.get.queryOptions({ taskId }));
  const campaign = useQuery(trpc.campaigns.get.queryOptions({ campaignId }));
  const others = useQuery(trpc.tasks.list.queryOptions({ campaignId, limit: 100 }));
  const members = useQuery(trpc.members.list.queryOptions({ limit: 100 }));
  const back = (
    <Link
      to="/w/$slug/campaigns/$campaignId"
      params={{ slug: workspace.slug, campaignId }}
      className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-text"
    >
      <ArrowLeft className="size-4" aria-hidden /> {campaign.data?.name ?? "Campaign"}
    </Link>
  );

  if (task.isPending) return <PageSpinner />;
  if (task.isError) {
    const code = getErrorCode(task.error);
    if (code === "NOT_FOUND" || code === "BAD_REQUEST")
      return (
        <EmptyState
          icon={<SearchX className="size-8" />}
          title="Task not found"
          description="It may have been removed, or the link is wrong."
          action={
            <Button asChild variant="secondary">
              <Link
                to="/w/$slug/campaigns/$campaignId"
                params={{ slug: workspace.slug, campaignId }}
              >
                Back to campaign
              </Link>
            </Button>
          }
        />
      );
    return <ErrorState error={task.error} onRetry={() => void task.refetch()} />;
  }

  const t = task.data;
  // RCM-01: the address names the wrong campaign; show the task under its own.
  if (t.campaignId !== campaignId)
    return (
      <Navigate
        to="/w/$slug/campaigns/$campaignId/tasks/$taskId"
        params={{ slug: workspace.slug, campaignId: t.campaignId, taskId: t.id }}
        replace
      />
    );
  const assignee = members.data?.items.find((m) => m.userId === t.assigneeUserId)?.name;
  const waitsOn = (others.data?.items ?? []).filter((o) => t.dependsOnTaskIds.includes(o.id));
  return (
    <>
      {back}
      <PageHeader
        title={t.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge>{TASK_STATUS_LABELS[t.status]}</Badge>
            {t.visibility === "agency_only" && <Badge tone="warning">Agency only</Badge>}
          </span>
        }
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <Card>
            <CardHeader title="Details" />
            <CardBody className="text-sm">
              {t.details ? (
                <p className="whitespace-pre-wrap">{t.details}</p>
              ) : (
                <p className="text-subtle">No details written yet.</p>
              )}
            </CardBody>
          </Card>
          <Conversation
            campaignId={campaignId}
            campaignArchived={campaign.data?.status === "archived"}
            target={{
              type: "task",
              id: t.id,
              label: t.title,
              agencyOnly: t.visibility === "agency_only",
            }}
          />
        </div>
        <Card>
          <CardHeader title="About" />
          <CardBody>
            <dl className="flex flex-col gap-4 text-sm">
              <Detail label="Who does it">{assignee ?? "No one yet"}</Detail>
              <Detail label="Due">{t.dueOn ? formatCalendarDate(t.dueOn) : "Not set"}</Detail>
              <Detail label="Waits on">
                {waitsOn.length > 0 ? (
                  <ul className="flex flex-col gap-1">
                    {waitsOn.map((o) => (
                      <li key={o.id}>
                        <Link
                          to="/w/$slug/campaigns/$campaignId/tasks/$taskId"
                          params={{ slug: workspace.slug, campaignId, taskId: o.id }}
                          className="text-primary hover:underline"
                        >
                          {o.title}
                        </Link>{" "}
                        <span className="text-subtle">({TASK_STATUS_LABELS[o.status]})</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  "Nothing"
                )}
              </Detail>
              <Detail label="Evidence">
                {t.evidenceLinks.length > 0 ? (
                  <ul className="flex flex-col gap-1">
                    {t.evidenceLinks.map((link) => (
                      <li key={link} className="break-all">
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  "None yet"
                )}
              </Detail>
              <Detail label="Created">{formatDateTime(t.createdAt)}</Detail>
            </dl>
          </CardBody>
        </Card>
      </div>
    </>
  );
}
